export function getSyncNodeIcon(type) {
  if (type === 'tag') return '#';
  if (type === 'asset') return 'A';
  if (type === 'insight') return 'I';
  if (type === 'note') return 'N';
  if (type === 'raw') return 'R';
  return 'W';
}

export function getSyncNodeSize(type, weight = 1, label = '') {
  const charCount = label.length || 0;
  
  if (type === 'tag') {
    // Tag: Compact base width. Scales slightly with importance (weight).
    const scale = Math.min(1.3, Math.max(1, 1 + Math.log10(weight || 1) * 0.12));
    const baseWidthFactor = 140 * scale;
    const textWidthFactor = charCount * 9 + 52;
    
    const width = Math.min(240, Math.max(baseWidthFactor, textWidthFactor));
    const height = 96; // Slightly shorter
    return { width: Math.round(width), height };
  }
  
  if (type === 'root') return { width: 104, height: 104 };

  // Assets, Hints, Notes: More compact base widths and text scaling
  const baseWidth = type === 'asset' || type === 'insight' ? 144 : 124;
  const height = type === 'asset' || type === 'insight' ? 104 : 84;
  const dynamicWidth = Math.min(260, Math.max(baseWidth, charCount * 9 + 32));
  
  return { width: Math.round(dynamicWidth), height };
}

export function getSyncNodeStyle(type, isSelected = false, weight = 1, label = '', status = '') {
  const isPending = type === 'asset' && status === 'pending';
  const base = {
    borderRadius: type === 'root' ? 999 : type === 'asset' || type === 'insight' || type === 'tag' ? 12 : 8,
    borderStyle: isPending ? 'dashed' : 'solid',
    borderWidth: isSelected ? 3 : 2,
    boxShadow: isSelected ? '0 0 0 2px rgba(255,255,255,0.32), 0 18px 40px rgba(0,0,0,0.35)' : '0 14px 30px rgba(0,0,0,0.28)',
    color: '#e5e7eb',
    display: 'flex',
    flexDirection: 'column',
    fontSize: 16,
    fontWeight: 900,
    lineHeight: 1.1,
    padding: 0,
    textAlign: 'center',
  };
  const palette = {
    root: { background: '#312e81', borderColor: '#818cf8', color: '#e0e7ff' },
    tag: { background: '#1e293b', borderColor: '#f59e0b', color: '#fde68a' },
    asset: isPending
      ? { background: '#064e3b', borderColor: '#059669', color: '#6ee7b7' }
      : { background: '#022c22', borderColor: '#10b981', color: '#a7f3d0' },
    insight: { background: '#312e81', borderColor: '#818cf8', color: '#c7d2fe' },
    note: { background: '#172554', borderColor: '#3b82f6', color: '#bfdbfe' },
    raw: { background: '#1f2937', borderColor: '#9ca3af', color: '#e5e7eb' },
  };

  return { ...base, ...(palette[type] || palette.note), ...getSyncNodeSize(type, weight, label) };
}

export function layoutRootTagNodes(childIds, nodesById) {
  const orderedIds = [...childIds].sort((a, b) => {
    const nodeA = nodesById.get(a);
    const nodeB = nodesById.get(b);
    const scoreA = nodeA?.importanceScore || 0;
    const scoreB = nodeB?.importanceScore || 0;
    return scoreB - scoreA || a.localeCompare(b, 'ja');
  });

  const innerCount = Math.min(orderedIds.length, 3);
  const outerCount = orderedIds.length - innerCount;

  const positioned = orderedIds
    .map((childId, index) => {
      const node = nodesById.get(childId);
      if (!node) return null;

      let radius, angle;
      if (index < innerCount) {
        radius = 110;
        angle = (index / Math.max(innerCount, 1)) * Math.PI * 2 - Math.PI / 2;
      } else {
        radius = 320;
        const outerIndex = index - innerCount;
        angle = (outerIndex / Math.max(outerCount, 1)) * Math.PI * 2 - Math.PI / 2;
      }

      return {
        ...node,
        x: Math.cos(angle) * radius,
        y: Math.sin(angle) * radius,
        scale: 1,
        isCenter: index < innerCount,
      };
    })
    .filter(Boolean);

  return positioned;
}

export function getSyncHandlePair(sourceNode, targetNode) {
  const dx = targetNode.x - sourceNode.x;
  const dy = targetNode.y - sourceNode.y;
  if (Math.abs(dx) > Math.abs(dy)) {
    return dx >= 0
      ? { sourceHandle: 'right-source', targetHandle: 'left-target' }
      : { sourceHandle: 'left-source', targetHandle: 'right-target' };
  }
  return dy >= 0
    ? { sourceHandle: 'bottom-source', targetHandle: 'top-target' }
    : { sourceHandle: 'top-source', targetHandle: 'bottom-target' };
}

export function filterTagsBySelectionAndQuery(tags, globalSelectedTags = [], mapSearchQuery = '') {
  let filteredTags = tags;

  if (globalSelectedTags.length > 0) {
    filteredTags = filteredTags.filter((tag) => globalSelectedTags.includes(tag.name));
  }

  if (mapSearchQuery) {
    const lowerQuery = mapSearchQuery.toLowerCase();
    filteredTags = filteredTags.filter((tag) => tag.name.toLowerCase().includes(lowerQuery));
  }

  return filteredTags;
}
